import { StyleSheet, Platform, Pressable, View, type ViewProps, LayoutChangeEvent } from "react-native";
import { useTheme } from "@/hooks/useTheme";
import { forwardRef, useState } from "react";
import Animated, { useAnimatedStyle, useSharedValue, withTiming, SharedValue, runOnJS } from "react-native-reanimated";
import { Gesture, GestureDetector } from "react-native-gesture-handler";

export type ResizerProps = ViewProps & {
  shared: SharedValue<number>;
  onUpdate?: (props?: any) => void;
  onEnd?: (props?: any) => void;
  parentLayout?: LayoutChangeEvent;
  ref?: React.Ref<View>;
  orienation?: "horizontal" | "vertical";
  min?: number;
  max?: number;
};

export const Resizer = forwardRef<View, ResizerProps>(
  ({ min, max, shared, onUpdate, onEnd, parentLayout, orienation = "vertical", style, ...otherProps }, ref) => {
    const [dragging, setDragging] = useState(false);
    const scale = useSharedValue(1);
    const translateX = useSharedValue(0);
    const translateY = useSharedValue(0);

    const animatedStyle = useAnimatedStyle(
      () =>
        ({
          transform: [{ scale: scale.get() }, { translateX: translateX.get() }, { translateY: translateY.get() }],
        }) as const,
    );

    const animatedStyleView = useAnimatedStyle(
      () =>
        ({
          position: "absolute",
          right: `${shared.get()}%`,
        }) as const,
    );
    const drag = Gesture.Pan()
      .onChange((event) => {
        if (!parentLayout) return;
        const parentWidth = parentLayout.nativeEvent.layout.width;
        const deltaPx = orienation === "vertical" ? event.changeX : event.changeY;
        const deltaPercent = (deltaPx / parentWidth) * 100;
        const nextValue = shared.get() - deltaPercent;
        if (typeof min === "number" && nextValue < min) return;
        if (typeof max === "number" && nextValue > max) return;
        setDragging(true);
        if (shared) {
          shared.set(nextValue);
        }
        if (shared && onUpdate) {
          runOnJS(onUpdate)?.(shared.get());
        }
      })
      .onEnd(() => {
        setDragging(false);
        if (onEnd) runOnJS(onEnd)?.();
      });

    const handleHoverIn = () => {
      if (Platform.OS === "web") {
        scale.set(withTiming(1.2, { duration: 150 }));
        if (orienation === "horizontal") translateX.set(withTiming(-1, { duration: 150 }));
        if (orienation === "vertical") translateX.set(withTiming(-1, { duration: 150 }));
      }
    };

    const handleHoverOut = () => {
      if (Platform.OS === "web") {
        scale.set(withTiming(1, { duration: 150 }));
        if (orienation === "horizontal") translateX.set(withTiming(0, { duration: 150 }));
        if (orienation === "vertical") translateX.set(withTiming(0, { duration: 150 }));
      }
    };

    const { colors } = useTheme();

    return (
      <GestureDetector gesture={drag}>
        <Animated.View style={[animatedStyleView, orienation === "vertical" ? styles.vertical : styles.horizontal]}>
          <Animated.View
            onPointerDown={() => setDragging(true)}
            onPointerUp={() => setDragging(false)}
            onPointerEnter={handleHoverIn}
            onPointerLeave={handleHoverOut}
            ref={ref}
            style={[
              styles.target,
              animatedStyle,
              { backgroundColor: colors.text },
              dragging && { backgroundColor: colors.primary },
              style,
            ]}
            {...otherProps}
          />
        </Animated.View>
      </GestureDetector>
    );
  },
);

Resizer.displayName = "Resizer";

const styles = StyleSheet.create({
  horizontal: {
    zIndex: 3,
    elevation: 3,
    height: 6,
    width: "100%",
    justifyContent: "center",
    opacity: 0.6,
  },
  vertical: {
    zIndex: 3,
    elevation: 3,
    justifyContent: "center",
    height: "100%",
    width: 8,
    opacity: 0.6,
    marginHorizontal: 2,
  },
  // pressable: {},
  target: {
    // position: "relative",
    right: 0,
    zIndex: 3,
    elevation: 3,
    borderRadius: 999,
    flexBasis: 90,
    opacity: 1,
  },
});
